import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Percent, RefreshCw, Save, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Admin panel — lets ops edit the APR and minimum stake per asset for the
 * staking products. Overrides live in the `staking_config` collection and
 * take effect for every NEW position (existing positions keep their
 * `apr_at_open`).
 */
export default function StakingAprPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/staking-config");
      setRows(r.data.products || []);
      setDirty({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load staking config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (asset, field, value) => {
    setRows((prev) => prev.map((r) => r.asset === asset ? { ...r, [field]: value } : r));
    setDirty((prev) => ({ ...prev, [asset]: { ...(prev[asset] || {}), [field]: value } }));
  };

  const save = async (asset) => {
    const changes = dirty[asset];
    if (!changes) return;
    setBusy((b) => ({ ...b, [asset]: "save" }));
    try {
      const payload = {};
      if (changes.apr_pct != null) {
        const n = Number(changes.apr_pct);
        if (!Number.isFinite(n) || n < 0 || n > 1000) {
          toast.error("APR must be between 0 and 1000%");
          setBusy((b) => ({ ...b, [asset]: null }));
          return;
        }
        payload.apr = n / 100;
      }
      if (changes.min_stake != null) {
        const n = Number(changes.min_stake);
        if (!Number.isFinite(n) || n < 0) {
          toast.error("Minimum stake must be ≥ 0");
          setBusy((b) => ({ ...b, [asset]: null }));
          return;
        }
        payload.min_stake = n;
      }
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing to save");
        return;
      }
      await api.put(`/admin/staking-config/${asset}`, payload);
      toast.success(`${asset} staking config updated`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy((b) => ({ ...b, [asset]: null }));
    }
  };

  const reset = async (asset) => {
    if (!confirm(`Reset ${asset} APR and min stake to defaults?`)) return;
    setBusy((b) => ({ ...b, [asset]: "reset" }));
    try {
      await api.delete(`/admin/staking-config/${asset}`);
      toast.success(`${asset} reset to default`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy((b) => ({ ...b, [asset]: null }));
    }
  };

  return (
    <div data-testid="admin-staking-panel" className="border border-[#2A2E38] bg-[#141720] p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E7587] flex items-center gap-2">
            <Sparkles size={11} /> Staking rates
          </div>
          <h3 className="font-display text-lg font-bold mt-1">Earn APR by asset</h3>
          <p className="text-xs text-[#A3A9B8] mt-1 max-w-2xl">
            Rates apply to every <strong className="text-[#E8E2D9]">new</strong> staking
            position. Existing positions keep the APR they were opened with. Tier
            bonuses (Nova +0.05%, VIP +0.10%) are added on top.
          </p>
        </div>
        <button
          data-testid="admin-staking-refresh"
          onClick={load}
          disabled={loading}
          className="text-xs px-3 py-1.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] inline-flex items-center gap-1"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587] border-b border-[#2A2E38]">
              <th className="px-3 py-2 text-left">Asset</th>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-right">APR (%)</th>
              <th className="px-3 py-2 text-right">Min stake</th>
              <th className="px-3 py-2 text-right">Default</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-6 text-center text-[#6E7587]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-[#6E7587]">No staking products</td></tr>
            )}
            {!loading && rows.map((row) => {
              const aprPctDisplay = dirty[row.asset]?.apr_pct ?? (row.apr * 100).toFixed(4);
              const minStakeDisplay = dirty[row.asset]?.min_stake ?? row.min_stake;
              const isDirty = !!dirty[row.asset];
              return (
                <tr key={row.asset} data-testid={`staking-row-${row.asset}`} className="border-b border-[#2A2E38] last:border-0">
                  <td className="px-3 py-3 font-mono">
                    <span className="text-[#F4F1EB] font-medium">{row.asset}</span>
                    {row.has_override && (
                      <span className="ml-2 text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 bg-[#D19B54]/15 text-[#D19B54] rounded-sm">
                        Custom
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-[#A3A9B8]">{row.name}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <input
                        data-testid={`staking-apr-${row.asset}`}
                        type="number"
                        step="0.01"
                        value={aprPctDisplay}
                        onChange={(e) => patch(row.asset, "apr_pct", e.target.value)}
                        className={`w-24 bg-[#0B0D12] border rounded-sm px-2 py-1.5 font-mono text-sm text-right focus:border-[#7C5CFF] focus:outline-none ${
                          isDirty && dirty[row.asset]?.apr_pct != null
                            ? "border-[#D19B54]"
                            : "border-[#2A2E38]"
                        }`}
                      />
                      <Percent size={12} className="text-[#6E7587]" />
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <input
                      data-testid={`staking-min-${row.asset}`}
                      type="number"
                      step="any"
                      value={minStakeDisplay}
                      onChange={(e) => patch(row.asset, "min_stake", e.target.value)}
                      className={`w-28 bg-[#0B0D12] border rounded-sm px-2 py-1.5 font-mono text-sm text-right focus:border-[#7C5CFF] focus:outline-none ${
                        isDirty && dirty[row.asset]?.min_stake != null
                          ? "border-[#D19B54]"
                          : "border-[#2A2E38]"
                      }`}
                    />
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-xs text-[#6E7587]">
                    <div>{(row.default_apr * 100).toFixed(2)}%</div>
                    <div>{row.default_min_stake}</div>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`staking-save-${row.asset}`}
                        disabled={!isDirty || busy[row.asset] === "save"}
                        onClick={() => save(row.asset)}
                        className="text-xs px-2.5 py-1.5 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:bg-[#2A2E38] disabled:text-[#6E7587] disabled:cursor-not-allowed inline-flex items-center gap-1"
                      >
                        {busy[row.asset] === "save" ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                        Save
                      </button>
                      <button
                        data-testid={`staking-reset-${row.asset}`}
                        disabled={!row.has_override || busy[row.asset] === "reset"}
                        onClick={() => reset(row.asset)}
                        className="text-xs px-2.5 py-1.5 border border-[#2A2E38] text-[#A3A9B8] rounded-sm hover:border-[#C75C5C] hover:text-[#C75C5C] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      >
                        <RefreshCw size={11} />
                        Reset
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
